import { AxiosResponse, AxiosError, isAxiosError } from 'axios';

class ApiResponse {
  public statusCode: number;
  public messages: Record<string, unknown>;
  public message: string;
  public data: Record<string, unknown>;
  public success: boolean;

  constructor(response: AxiosResponse | AxiosError | undefined) {
    // Defensive default — covers undefined/null/network-abort cases.
    if (!response) {
      this.success = false;
      this.messages = {};
      this.message = 'Something went wrong. Please try again.';
      this.data = {};
      this.statusCode = 500;
      return;
    }

    // Use Axios's own type-guard instead of the fragile 'response' in obj check.
    if (isAxiosError(response)) {
      const axiosError = response as AxiosError;
      const errorDataObject = axiosError.response?.data as
        | Record<string, unknown>
        | undefined;

      const parsedMessage =
        errorDataObject && errorDataObject['message']
          ? (errorDataObject['message'] as Record<string, string>)
          : { error: [axiosError.message ?? 'An error occurred'] };

      this.success = false;
      this.data =
        errorDataObject && errorDataObject['data']
          ? (errorDataObject['data'] as Record<string, unknown>)
          : (errorDataObject ?? {});
      this.messages = this.data;
      this.message = this.getFirstMessage(parsedMessage);
      this.statusCode = axiosError.response?.status ?? 500;
    } else {
      // Genuine AxiosResponse (HTTP success).
      const axiosResponse = response as AxiosResponse;
      const data = axiosResponse?.data;
      this.success = data?.success ?? false;
      this.messages = (data?.message ?? {}) as Record<string, unknown>;
      this.message = this.getFirstMessage(
        data?.message as Record<string, unknown>
      );
      // data?.data guards against an empty/null response body.
      this.data = (data?.data ?? {}) as Record<string, unknown>;
      this.statusCode = axiosResponse?.status ?? 500;
    }
  }

  private getFirstMessage(messages: Record<string, any>): string {
    let message = '';
    if (!messages) return message;
    // Server sometimes sends a plain string instead of a keyed object.
    if (typeof messages === 'string') return messages;
    try {
      const keys = Object.keys(messages);
      if (keys.length > 0) {
        const values = messages[keys[0]];
        if (Array.isArray(values) && values.length > 0) {
          message = values[0];
        } else if (typeof values === 'string') {
          message = values;
        }
      }
    } catch (error) {
      console.error('An error occurred while processing the messages:', error);
    }
    return message;
  }

  getStatusCode(): number {
    return this.statusCode;
  }

  getMessages(): Record<string, unknown> {
    return this.messages;
  }

  getMessage(): string {
    return this.message;
  }

  getData(): Record<string, unknown> {
    return this.data;
  }

  getSuccess(): boolean {
    return this.success;
  }
}

export default ApiResponse;
